import { useEffect, useState } from 'react'
import {
  gmailAuthUrl,
  listConnections,
  plaidExchange,
  plaidLinkToken,
  plaidSync,
  type ConnectionsResponse,
} from '../lib/api'

declare global {
  interface Window {
    Plaid?: {
      create(opts: any): { open(): void; exit(): void; destroy(): void }
    }
  }
}

export function Connections() {
  const [data, setData] = useState<ConnectionsResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const reload = () =>
    listConnections()
      .then(setData)
      .catch((e) => setErr(e?.message || 'failed'))

  useEffect(() => {
    reload()
  }, [])

  const connectPlaid = async () => {
    setErr(null)
    setInfo(null)
    try {
      const { link_token } = await plaidLinkToken()
      if (!window.Plaid) {
        setErr('Plaid Link.js not loaded — check index.html script tag')
        return
      }
      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token: string, metadata: any) => {
          try {
            await plaidExchange(
              public_token,
              metadata?.institution?.name,
              metadata?.institution?.institution_id,
            )
            setInfo('connected.')
            await plaidSync().catch(() => {})
            reload()
          } catch (e: any) {
            setErr(e?.message || 'exchange failed')
          }
        },
        onExit: (err: any, _metadata: any) => {
          if (err) setErr(err.display_message || err.error_message || 'plaid link cancelled')
        },
      })
      handler.open()
    } catch (e: any) {
      setErr(e?.message || 'plaid link failed')
    }
  }

  const connectGmail = async () => {
    setErr(null)
    try {
      const label = prompt('Label for this gmail account (e.g. mason-personal):')
      if (!label) return
      const { authorization_url } = await gmailAuthUrl(label.trim())
      window.location.href = authorization_url
    } catch (e: any) {
      setErr(e?.message || 'gmail auth url failed')
    }
  }

  const sync = async () => {
    setErr(null)
    try {
      const r = await plaidSync()
      setInfo(`synced ${r.items_synced} item(s).`)
      reload()
    } catch (e: any) {
      setErr(e?.message || 'sync failed')
    }
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-light text-zinc-100">connections</h1>

      {err && <div className="text-red-400 text-sm">{err}</div>}
      {info && <div className="text-emerald-500 text-sm">{info}</div>}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] uppercase tracking-widest text-zinc-500">banks (plaid)</h2>
          <div className="flex gap-2">
            <button
              onClick={sync}
              className="px-3 py-1.5 rounded border border-zinc-800 text-zinc-300 text-xs hover:bg-zinc-900"
            >
              sync now
            </button>
            <button
              onClick={connectPlaid}
              className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-xs font-medium hover:bg-white"
            >
              + connect a bank
            </button>
          </div>
        </div>
        {!data?.plaid_items?.length ? (
          <Empty>no banks linked.</Empty>
        ) : (
          <ul className="divide-y divide-zinc-900 border border-zinc-900 rounded">
            {data.plaid_items.map((p) => (
              <li key={p.id} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-zinc-200">{p.institution_name || '(unknown institution)'}</div>
                  <div className="text-[11px] text-zinc-500">
                    item {p.item_id.slice(0, 8)}…
                    {p.last_sync_at && ` · synced ${new Date(p.last_sync_at).toLocaleString()}`}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 uppercase">{p.status}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] uppercase tracking-widest text-zinc-500">email (gmail)</h2>
          <button
            onClick={connectGmail}
            className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-xs font-medium hover:bg-white"
          >
            + connect gmail
          </button>
        </div>
        {!data?.connections?.filter((c) => c.provider === 'gmail').length ? (
          <Empty>no gmail accounts linked.</Empty>
        ) : (
          <ul className="divide-y divide-zinc-900 border border-zinc-900 rounded">
            {data.connections
              .filter((c) => c.provider === 'gmail')
              .map((c) => (
                <li key={c.id} className="px-4 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-zinc-200">{c.label}</div>
                    <div className="text-[11px] text-zinc-500">
                      added {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 uppercase">{c.status}</div>
                </li>
              ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-dashed border-zinc-900 rounded px-4 py-6 text-center text-zinc-600 text-sm">
      {children}
    </div>
  )
}
